import { addDays, todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { uid } from '../../../../lib/uid';
import type { PersonNameParts } from '../../../document-management/routing/routingTypes';

/**
 * A synthetic press conference, for filling the form in one click — the form's own state shape
 * (`HH:mm` times, as the time input holds them).
 *
 * **The requestor's name LANDS IN `rsys_pii`, so it must not look like a person** — `Placeholder
 * Testcase`, suffixed. Speakers and an internal requestor are picked by id from the LIVE personnel
 * feed the form already loaded; a parameterless generator would fill none of them.
 */
export function samplePressConference(personnelIds: string[]): {
  purpose: string;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  venue: string;
  remarks: string;
  organization: string;
  requestorName: PersonNameParts;
  requestorPersonnelId: string | null;
  speakerPersonnelIds: string[];
} {
  const topic = pick(TOPICS);
  const slot = pick(SLOTS);
  const suffix = uid().slice(0, 6).toUpperCase();

  return {
    purpose: `${topic} ${suffix}`,

    // Spread either side of today, so a run of clicks fills past and coming months alike.
    scheduledDate: addDays(todayCalendarDate(), Math.floor(Math.random() * 120) - 30),
    startTime: slot.start,
    endTime: slot.end,
    venue: pick(VENUES),
    remarks: pick(REMARKS),
    organization: `${pick(ORGANIZATIONS)} ${suffix}`,
    requestorName: { salutation: '', firstName: 'Placeholder', middleName: '', lastName: `Testcase ${suffix}`, suffix: '' },
    // Filled behind the External choice the generator makes, for whoever switches to Internal.
    requestorPersonnelId: personnelIds.length > 0 ? pick(personnelIds) : null,
    speakerPersonnelIds: [...personnelIds].sort(() => Math.random() - 0.5).slice(0, 2),
  };
}

function pick<T>(pool: readonly T[]): T {
  return pool[Math.floor(Math.random() * pool.length)];
}

const TOPICS = [
  'Quarterly accomplishment briefing',
  'Typhoon preparedness update',
  'Launch of the online services portal',
  'Year-end performance report',
  'Joint operations announcement',
] as const;

const SLOTS = [
  { start: '09:00', end: '10:30' },
  { start: '10:00', end: '11:00' },
  { start: '14:00', end: '15:30' },
] as const;

const VENUES = ['Main Hall, Ground Floor', 'Media Center', 'Conference Room B', 'Multi-purpose Hall'] as const;

const REMARKS = [
  'Opening statement, then questions from accredited media. Press kits at the registration table.',
  'Accreditation closes the day before. Live-streamed on the official page.',
  'Brief remarks followed by a short open forum.',
] as const;

const ORGANIZATIONS = ['Example Broadcasting Network', 'Sample Press Association', 'Placeholder News Bureau'] as const;
